import React, { useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useI18next } from 'gatsby-plugin-react-i18next';
import { Helmet, HelmetProps } from 'react-helmet';
import classNames from 'classnames';

type SeoProps = {
  bodyAttributes?: HelmetProps['bodyAttributes'];
  description?: string | null;
  lang?: string;
  meta?: HelmetProps['meta'];
  script?: any[];
  title: string;
};

export const Seo = ({
  bodyAttributes,
  description,
  lang,
  meta = [],
  script = [],
  title,
}: SeoProps) => {
  const { language } = useI18next();
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
        }
      }
    }
  `);

  const metaDescription = description || site.siteMetadata.description;
  const defaultTitle = site.siteMetadata?.title;

  useEffect(() => {
    const handleScroll = () => {
      const body = document.querySelector('body');
      if (body) {
        if (window.scrollY > 0) {
          body.classList.add('scrolled');
        } else {
          body.classList.remove('scrolled');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const combinedClassNames = classNames('scrolled', bodyAttributes?.class);

  return (
    <Helmet
      bodyAttributes={{
        class: combinedClassNames,
      }}
      htmlAttributes={{
        lang: lang ?? language,
      }}
      title={title}
      titleTemplate={defaultTitle ? `%s | ${defaultTitle}` : undefined}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        ...meta,
      ]}
      script={script}
    />
  );
};

export const query = graphql`
  fragment Seo on STRAPI__COMPONENT_SHARED_SEO {
    metaDescription
    metaTitle
  }
`;
